<template>
  <BaseModal
    :is-open="isOpen"
    title="약관 상세보기"
    :show-default-footer="false"
    :close-on-overlay-click="true"
    :close-on-escape="true"
    content-padding="none"
    @close="handleClose"
    @update:is-open="$emit('update:isOpen', $event)"
  >
    <div class="flex max-h-[80vh] min-h-[600px] flex-col">
      <div class="space-y-6 p-6">
        <!-- 업데이트 날짜 선택 -->
        <div class="space-y-2">
          <div class="w-[320px]">
            <BaseInputSelect
              v-model="selectedTerms"
              :options="termsOptions"
              class="w-full"
              size="md"
              :teleport="false"
              :disabled="disabledTermsType"
            />
          </div>
          <div class="w-[225px]">
            <BaseInputSelect
              v-model="selectedVersion"
              :options="versionOptions"
              class="w-full"
              :teleport="false"
            />
          </div>
        </div>

        <!-- 약관 내용 -->
        <div v-if="loading" class="max-h-[500px] space-y-6 overflow-y-auto">
          <div class="flex items-center justify-center py-8">
            <p class="text-sm text-[var(--font-color-muted)]">약관을 불러오는 중...</p>
          </div>
        </div>
        <div v-else-if="error" class="max-h-[500px] space-y-6 overflow-y-auto">
          <div class="flex items-center justify-center py-8">
            <p class="text-red text-sm">{{ error }}</p>
          </div>
        </div>
        <div v-else-if="selectedTermsContent" class="max-h-[500px] space-y-6 overflow-y-auto">
          <BaseToastEditorContent :modelValue="selectedTermsContent.detail" />
        </div>
        <div v-else class="max-h-[500px] space-y-6 overflow-y-auto">
          <div class="flex items-center justify-center py-8">
            <p class="text-sm text-[var(--font-color-muted)]">약관을 선택해주세요.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 푸터 영역 -->
    <template v-if="props.showFooterActions" #footer>
      <div class="flex gap-3 py-4">
        <BaseButton
          label="닫기"
          variant="outlined"
          color="grey"
          size="sm"
          full-width
          @click="handleClose"
        />
        <BaseButton
          label="동의함"
          variant="contained"
          color="primary"
          size="sm"
          full-width
          @click="handleAgree"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import {
  getTermsLabel,
  getTermsDialogKey,
  getTermsTypeFromDialogKey,
  type AllTermsType,
} from '@/constants/terms';
import { BaseModal, BaseButton, BaseInputSelect, BaseToastEditorContent } from '@template/ui';
import { useTerm } from '@/composables/term/useTerm';
import { ref, computed, watch } from 'vue';

interface Props {
  isOpen: boolean;
  showFooterActions?: boolean;
  disabledTermsType?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'agree', value: string): void;
  (e: 'update:isOpen', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  showFooterActions: true,
  disabledTermsType: false,
});
const emit = defineEmits<Emits>();

const selectedTab = defineModel<string>('selectedTab', {});

const {
  loading,
  error,
  termsDetail,
  requiredTerms,
  optionalTerms,
  loadTermsDetail,
  loadTermsList,
} = useTerm();

// 선택된 약관 버전 (effectiveDate)
const selectedVersion = ref<string>('');
const selectedTerms = ref<string>('');

/**
 * 약관 타입 옵션 (동적 생성)
 * Open API 모드(disabledTermsType)일 때 Open API 약관을 옵션에 포함
 */
const termsOptions = computed(() => {
  const required = requiredTerms.value.map((termsType) => ({
    value: getTermsDialogKey(termsType as AllTermsType),
    label: `${getTermsLabel(termsType as AllTermsType)} (필수)`,
  }));

  const optional = optionalTerms.value.map((termsType) => ({
    value: getTermsDialogKey(termsType as AllTermsType),
    label: `${getTermsLabel(termsType as AllTermsType)} (선택)`,
  }));

  let options = [...required, ...optional];

  if (props.disabledTermsType) {
    const openApiOption = {
      value: getTermsDialogKey('OPEN_API_TERMS_OF_USE'),
      label: `${getTermsLabel('OPEN_API_TERMS_OF_USE')} (필수)`,
    };
    if (!options.some((o) => o.value === openApiOption.value)) {
      options = [openApiOption, ...options];
    }
  }

  return options;
});

/**
 * 버전 옵션 (약관의 effectiveDate 기준으로 생성)
 */
const versionOptions = computed(() => {
  if (!termsDetail.value || termsDetail.value.length === 0) {
    return [];
  }

  // 날짜순 정렬 (최신순)
  const sortedTerms = [...termsDetail.value].sort(
    (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
  );

  const formatDateOnly = (dateStr: string) => dateStr?.split('T')[0] ?? dateStr;

  return sortedTerms.map((term, index) => ({
    value: term.effectiveDate,
    label:
      index === 0
        ? `최근 업데이트: ${formatDateOnly(term.effectiveDate)}`
        : `이전 버전: ${formatDateOnly(term.effectiveDate)}`,
  }));
});

/**
 * 선택된 버전의 약관 내용
 */
const selectedTermsContent = computed(() => {
  if (!selectedVersion.value || !termsDetail.value) {
    return null;
  }

  return termsDetail.value.find((term) => term.effectiveDate === selectedVersion.value) || null;
});

/**
 * selectedTab(dialogKey)이 변경될 때 약관 조회
 */
watch(
  selectedTab,
  async (newDialogKey) => {
    if (!newDialogKey) return;

    // dialogKey를 TermsType으로 변환
    const termsType = getTermsTypeFromDialogKey(newDialogKey);
    if (!termsType) return;

    selectedTerms.value = newDialogKey;

    // 약관 상세 정보 조회
    await loadTermsDetail(termsType);

    // 최신 버전 자동 선택
    if (termsDetail.value && termsDetail.value.length > 0) {
      const sortedTerms = [...termsDetail.value].sort(
        (a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
      );
      selectedVersion.value = sortedTerms[0].effectiveDate;
    }
  },
  { immediate: true }
);

/**
 * selectedTerms(드롭다운 선택)이 변경될 때 selectedTab도 업데이트
 */
watch(selectedTerms, (newTermsDialogKey) => {
  if (newTermsDialogKey && selectedTab.value !== newTermsDialogKey) {
    selectedTab.value = newTermsDialogKey;
  }
});

/**
 * 다이얼로그가 열릴 때 약관 목록 조회
 */
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      await loadTermsList();
    }
  },
  { immediate: true }
);

const handleClose = () => {
  emit('close');
  emit('update:isOpen', false);
};

const handleAgree = () => {
  emit('agree', selectedTab.value || '');
  handleClose();
};
</script>
